/**
 * Route-level permission check.
 *
 * This is presentation, not enforcement. A user who reaches a URL they lack the permission for gets a clear
 * refusal instead of an empty screen — but the reason they see nothing is that RLS returns nothing and the
 * command functions refuse. Removing this component would change the message, not the security.
 */
import type { ReactNode } from 'react';
import { ShieldOff } from 'lucide-react';
import { useSession } from '../lib/session';
import { Page } from '../shell/AppShell';
import { EmptyState, LoadingRows, Panel, StatusBadge } from '../ui/primitives';

export function RequirePermission({
  permission, screenLabel, children,
}: {
  permission: string;
  screenLabel: string;
  children: ReactNode;
}) {
  const { can, isLoading, me } = useSession();

  if (isLoading) {
    return (
      <Page title={screenLabel}>
        <Panel>
          <LoadingRows />
        </Panel>
      </Page>
    );
  }

  if (me && me.authenticated === false) {
    return (
      <Page title="Sign in required">
        <Panel>
          <EmptyState
            title="You are not signed in"
            detail="Accounts are created by invitation. Sign in to continue."
          />
        </Panel>
      </Page>
    );
  }

  if (!can(permission)) {
    return (
      <Page title={screenLabel} actions={<StatusBadge tone="refused" icon={ShieldOff}>No access</StatusBadge>}>
        <Panel>
          <EmptyState
            title="You do not have access to this screen"
            detail={
              <>
                It needs the <code className="code-text">{permission}</code> permission. Ask an administrator
                if you need it — access is granted by role, and every grant is recorded.
              </>
            }
          />
        </Panel>
      </Page>
    );
  }

  return <>{children}</>;
}
